'use client';

import React from 'react';
import { Button, LucideIcon, copyToast } from '@skin-seoul/design-system';
import {
  ORDER_STATUS_COLORS,
  DEFAULT_ORDER_STATUS_COLOR,
  formatCurrency,
  type OrderDisplay,
  type OrderDisplayItem,
} from '@skin-seoul/service';
import { OrderTrackingInfo } from './OrderTrackingInfo';

interface OrderCardProps {
  order: OrderDisplay;
  onClickWriteReview?: (item: OrderDisplayItem) => void;
}

const copyOrderNumber = (text: string) => {
  const orderNumberWithoutHash = text.replace(/^#/, '');
  navigator.clipboard.writeText(orderNumberWithoutHash);
  copyToast('Order number copied');
};

/** Map Chakra color tokens to Tailwind bg classes */
const STATUS_COLOR_TO_BG: Record<string, string> = {
  'success.600': 'bg-success-600',
  'warning.300': 'bg-warning-300',
  'error.600': 'bg-error-600',
  'neutral.400': 'bg-neutral-400',
};

export const OrderCard = ({ order, onClickWriteReview }: OrderCardProps) => {
  const statusColor = ORDER_STATUS_COLORS[order.status] ?? DEFAULT_ORDER_STATUS_COLOR;
  const statusBgClass = STATUS_COLOR_TO_BG[statusColor] ?? 'bg-neutral-400';

  return (
    <div className="flex flex-col w-full bg-generic-0 border border-neutral-100 rounded-[8px] overflow-hidden gap-0">
      <div className="flex flex-col gap-3 p-3 w-full items-start">
        <div className="flex justify-between items-end w-full">
          <div className="flex flex-col gap-1 items-start">
            <div className="flex flex-row items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${statusBgClass}`} />
              <p className="text-heading-h5-mobile font-bold text-neutral-600 capitalize">{order.status}</p>
            </div>
            <p className="text-label-small font-normal text-neutral-400">Ordered on {order.orderDate}</p>
          </div>
          {order.receiptUrl && (
            <a href={order.receiptUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
              <p className="text-label-small font-medium text-neutral-400 underline cursor-pointer hover:opacity-80">
                Receipt
              </p>
            </a>
          )}
        </div>

        <div className="flex bg-primary-50 border border-primary-100 rounded-[8px] p-3 justify-between w-full">
          <div className="flex flex-col gap-1 items-start flex-1">
            <p className="text-label-small font-normal text-neutral-400">Order Number</p>
            <div className="flex flex-row gap-1">
              <p className="text-label-medium font-medium text-neutral-700">{order.orderNumber}</p>
              <div
                className="cursor-pointer hover:opacity-80 w-4 h-4"
                onClick={() => copyOrderNumber(order.orderNumber)}
              >
                <LucideIcon name="Copy" size={16} />
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-1 items-end">
            <p className="text-label-small font-normal text-neutral-400">Total</p>
            <p className="text-label-medium font-medium text-neutral-700">
              {formatCurrency(order.currency, order.total)}
            </p>
          </div>
        </div>

        {order.items.map((item) => (
          <div key={item.id} className="flex flex-row gap-3 w-full">
            <div className="w-[72px] h-[72px] border border-neutral-75 rounded-[8px] overflow-hidden shrink-0">
              <div className="w-16 h-16 rounded-[8px] overflow-hidden">
                <img src={item.imageUrl} alt={item.name} className="w-full h-full object-contain mix-blend-multiply" />
              </div>
            </div>

            <div className="flex flex-col gap-3 flex-1 items-start">
              <div className="flex flex-col gap-1 w-full items-start">
                <p className="text-label-small font-semibold text-neutral-700">{item.name}</p>
                <div className="flex flex-row justify-between w-full">
                  <div className="flex flex-row gap-1">
                    <p className="text-paragraph-xsmall font-normal text-neutral-500">{item.variant}</p>
                    <p className="text-paragraph-xsmall font-normal text-neutral-500">Qty:{item.quantity}</p>
                  </div>
                  <p className="text-paragraph-xsmall font-normal text-neutral-500">
                    {formatCurrency(order.currency, item.price)}
                  </p>
                </div>
              </div>
              <Button
                className="bg-neutral-50 text-neutral-500 rounded-[4px] border-neutral-100 font-regular text-paragraph-xsmall px-3 py-1 h-[28px] hover:bg-neutral-100"
                variant="outline"
                size="sm"
                onClick={() => onClickWriteReview?.(item)}
              >
                Write Review
              </Button>
            </div>
          </div>
        ))}

        <OrderTrackingInfo
          deliveryDate={order.deliveryDate}
          trackingNumber={order.trackingNumber}
          carrier={order.carrier}
          trackingEvents={order.trackingEvents}
        />
      </div>
    </div>
  );
};
